/**
 * Utilities for parsing Matrix mentions from HTML formatted_body content
 */

/**
 * Parse Matrix.to mention links from HTML and extract structured mention data.
 * Used for rendering mention pills in message displays.
 *
 * @param {string} html - HTML string containing Matrix.to anchor links
 * @returns {Array<Object>} Array of mention objects with userId, displayText, position info
 *
 * @example
 * const html = 'Hello <a href="https://matrix.to/#/@alice:matrix.org">Alice</a>!';
 * const mentions = parseMentionsFromHtml(html);
 * // Returns: [{ userId: '@alice:matrix.org', displayText: 'Alice', start: 6, end: 62, fullMatch: '<a href="...">Alice</a>' }]
 */
export function parseMentionsFromHtml(html) {
  if (!html || typeof html !== 'string') {
    return [];
  }

  // Match Matrix.to anchor tags for users (@), rooms (!), and aliases (#)
  // Format: <a href="https://matrix.to/#/@user:server">Display Text</a>
  // Also match URL-encoded prefixes: %40 (@), %21 (!), %23 (#)
  const regex = /<a\s+href="https:\/\/matrix\.to\/#\/([@!#%][^"]+)"[^>]*>([^<]+)<\/a>/gi;
  const mentions = [];
  let match;

  while ((match = regex.exec(html)) !== null) {
    const encodedUserId = match[1];
    const displayText = match[2];

    // Decode URL-encoded user IDs (e.g., %40 -> @)
    let userId;
    try {
      userId = decodeURIComponent(encodedUserId);
    } catch {
      // If decoding fails, use as-is
      userId = encodedUserId;
    }

    mentions.push({
      userId,
      displayText,
      start: match.index,
      end: match.index + match[0].length,
      fullMatch: match[0],
    });
  }

  return mentions;
}

/**
 * Check if an HTML string contains any Matrix.to mention links
 *
 * @param {string} html - HTML string to check
 * @returns {boolean} True if the HTML contains mention links
 */
export function hasMentions(html) {
  if (!html || typeof html !== 'string') {
    return false;
  }
  return /href="https:\/\/matrix\.to\/#\/[@!#]/.test(html);
}

/**
 * Check if an HTML string contains a mention of a specific user
 *
 * @param {string} html - HTML string to check
 * @param {string} userId - Matrix user ID to look for
 * @returns {boolean} True if the HTML contains a mention of the user
 */
export function hasMentionForUser(html, userId) {
  if (!html || !userId) {
    return false;
  }

  const mentions = parseMentionsFromHtml(html);
  return mentions.some((mention) => mention.userId === userId);
}
